import { ThemeEvents, CartErrorEvent, CartUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

// ---------------------------------------------------------------------------
// BundleBuilder — interactive bundle slots + JSON add-to-cart
// ---------------------------------------------------------------------------
class BundleBuilder extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {boolean} */
  #bundleAddInFlight = false;

  /** @type {number | undefined} */
  #cartErrorHideTimeoutId;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.discount1 = parseInt(this.dataset.discountOne, 10) || 0;
    this.discount2 = parseInt(this.dataset.discountTwo, 10) || 0;

    try {
      const dataScript = this.querySelector('.bundle-builder__data');
      this.bundleData = dataScript ? JSON.parse(dataScript.textContent).slots : [];
    } catch {
      this.bundleData = [];
    }

    try {
      const mainScript = this.querySelector('.bundle-builder__main-variants');
      this.mainVariants = mainScript ? JSON.parse(mainScript.textContent) : [];
    } catch {
      this.mainVariants = [];
    }

    this.totalsBlock = this.querySelector('.bundle-builder__totals');
    this.totalsLabel = this.querySelector('.bundle-builder__totals-label');
    this.totalsPrice = this.querySelector('.bundle-builder__totals-price');
    this.totalsCompare = this.querySelector('.bundle-builder__totals-compare');

    this.querySelectorAll('.bundle-builder__trigger').forEach((trigger) => {
      trigger.addEventListener(
        'click',
        (e) => {
          if (e.target.closest('.bundle-builder__trigger-select .bundle-builder__trigger-icon')) {
            e.preventDefault();
            e.stopPropagation();
            this.deselectSlot(trigger);
            return;
          }
          this.openSlot(trigger);
        },
        { signal }
      );
    });

    this.querySelectorAll('.bundle-builder__select--product').forEach((select) => {
      select.addEventListener('change', (e) => this.onProductChange(e.target), { signal });
    });

    this._productChoicesInited = false;
    const runChoices = () => {
      if (typeof window.Choices !== 'function') return;
      this.initProductChoices();
    };
    if (typeof window.Choices === 'function') {
      runChoices();
    } else {
      window.addEventListener('load', runChoices, { signal });
    }

    this.querySelectorAll('.bundle-builder__select--variant').forEach((select) => {
      select.addEventListener('change', () => this.updateTotals(), { signal });
    });

    const ctx = this.findProductFormContext();
    if (ctx?.form) {
      ctx.form.addEventListener('submit', this.#onProductFormSubmit, { capture: true, signal });
    }

    const listenRoot = this.getProductFlowRoot();
    listenRoot?.addEventListener(ThemeEvents.variantUpdate, () => this.updateTotals(), { signal });

    const bundleNotice = this.querySelector('.bundle-builder__totals .price__notice');
    if (bundleNotice) {
      const productPrice = listenRoot?.querySelector('.product__price .price, .product__price');
      productPrice?.querySelector('.price__notice')?.remove();
    }

    this.updateTotals();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
  }

  getProductFlowRoot() {
    return this.closest('[data-product-grid-content]') || this.closest('.shopify-section');
  }

  /**
   * @returns {{ productFormComponent: HTMLElement, form: HTMLFormElement, variantInput: HTMLInputElement | null, section: Element | null } | null}
   */
  findProductFormContext() {
    const section = this.getProductFlowRoot();
    const productFormComponent = section?.querySelector('product-form-component');
    if (!productFormComponent) return null;
    const form = productFormComponent.querySelector('form');
    if (!form) return null;
    const variantInput = form.querySelector('input[name="id"]');
    return { productFormComponent, form, variantInput, section };
  }

  /** Live DOM button — refs can lag behind after morph/MutationObserver updates. */
  #getAddToCartButton(form) {
    if (!form) return null;
    return form.querySelector('button[type="submit"][name="add"]');
  }

  #getBundleVariantSelectPlaceholder() {
    return (
      (typeof Theme !== 'undefined' && Theme.translations?.bundle_variant_select_placeholder) ||
      '— Select a color —'
    );
  }

  /** Clears variant `<select>` options and restores the translated placeholder row. */
  #resetVariantSelectPlaceholder(variantSelect) {
    variantSelect.innerHTML = '';
    const opt = document.createElement('option');
    opt.value = '';
    opt.textContent = this.#getBundleVariantSelectPlaceholder();
    variantSelect.appendChild(opt);
  }

  /**
   * @param {HTMLFormElement} form
   */
  shouldDeferProductFormSubmit(form) {
    const ctx = this.findProductFormContext();
    if (!form || !ctx || ctx.form !== form) return false;
    return this.getSelectedVariants().length > 0;
  }

  /** @param {SubmitEvent} e */
  #onProductFormSubmit = (e) => {
    const bundleVariantIds = this.getSelectedVariants()
      .map((id) => parseInt(id, 10))
      .filter((id) => Number.isFinite(id));
    if (bundleVariantIds.length === 0) return;

    const ctx = this.findProductFormContext();
    if (!ctx || e.target !== ctx.form) return;

    const addBtn = this.#getAddToCartButton(ctx.form);
    if (addBtn?.disabled) return;
    if (this.#bundleAddInFlight) return;

    e.preventDefault();
    e.stopPropagation();

    this.doBundleAddToCart(ctx, bundleVariantIds, e);
  };

  getCartSectionsParameter() {
    const seen = new Set();
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        const sid = item.dataset.sectionId;
        if (!seen.has(sid)) {
          seen.add(sid);
          ids.push(sid);
        }
      }
    });
    // Cart bundled section rendering allows at most five sections per request (Shopify limit).
    const limited = ids.slice(0, 5);
    return limited.length ? limited.join(',') : null;
  }

  /**
   * @param {{ section?: Element | null, variantInput?: HTMLInputElement | null } | null} ctx
   */
  getResolvedMainVariantId(ctx) {
    if (ctx?.section) {
      const mainPicker = ctx.section.querySelector(
        'variant-picker[data-template-product-match="true"], variant-picker[ref="mainVariantPicker"]'
      );
      const fromPickerEl = mainPicker?.querySelector('fieldset input:checked, select option[selected]');
      const fromPicker = fromPickerEl?.dataset?.variantId;
      if (fromPicker) {
        const n = parseInt(fromPicker, 10);
        if (Number.isFinite(n)) return n;
      }
    }
    const v = ctx?.variantInput?.value;
    const n = parseInt(String(v ?? '').trim(), 10);
    return Number.isFinite(n) ? n : null;
  }

  /**
   * @param {HTMLElement} productFormComponent
   * @param {string} message
   */
  #showBundleCartError(productFormComponent, message) {
    window.dispatchEvent(new CartErrorEvent(productFormComponent.id, message));

    const addToCartTextError = productFormComponent.refs?.addToCartTextError;
    if (addToCartTextError) {
      addToCartTextError.classList.remove('hidden');
      const textNode = addToCartTextError.childNodes[2];
      if (textNode) {
        textNode.textContent = message;
      } else {
        addToCartTextError.appendChild(document.createTextNode(message));
      }
    }

    const liveRegion = productFormComponent.refs?.liveRegion;
    if (liveRegion) liveRegion.textContent = message;

    if (this.#cartErrorHideTimeoutId) clearTimeout(this.#cartErrorHideTimeoutId);
    this.#cartErrorHideTimeoutId = window.setTimeout(() => {
      if (addToCartTextError) addToCartTextError.classList.add('hidden');
      if (liveRegion) liveRegion.textContent = '';
      this.#cartErrorHideTimeoutId = undefined;
    }, 10000);
  }

  /**
   * Refresh header cart bubble (and optional section HTML) from the real cart.
   * Use after bundle add success or failure so OOS / API errors never leave a stale or fake count.
   *
   * @param {HTMLElement} productFormComponent
   * @param {Record<string, string | null | undefined> | undefined} sections
   * @param {{ afterFailure?: boolean }} [options]
   */
  #syncCartUiFromServer(productFormComponent, sections, options) {
    const afterFailure = Boolean(options?.afterFailure);
    const cartJsUrl = `${Theme.routes.root}cart.js`;
    const headers = { Accept: 'application/json' };

    const fetchCartJson = () =>
      fetch(cartJsUrl, { headers }).then((r) => {
        if (r.status === 429 || r.status === 503) {
          return new Promise((resolve) => {
            window.setTimeout(() => {
              resolve(
                fetch(cartJsUrl, { headers }).then((r2) => {
                  if (!r2.ok) {
                    const err = new Error(String(r2.status));
                    err.status = r2.status;
                    throw err;
                  }
                  return r2.json();
                })
              );
            }, 1500);
          });
        }
        if (!r.ok) {
          const err = new Error(String(r.status));
          err.status = r.status;
          throw err;
        }
        return r.json();
      });

    fetchCartJson()
      .then((cart) => {
        document.dispatchEvent(
          new CartUpdateEvent({}, productFormComponent.id, {
            itemCount: cart.item_count,
            source: 'cart-items-component',
            productId: productFormComponent.dataset.productId,
            sections,
            ...(afterFailure ? { didError: true } : {}),
          })
        );
      })
      .catch((err) => {
        const status = typeof err.status === 'number' ? err.status : parseInt(String(err.message), 10);
        if (status === 429 || status === 503) {
          console.warn('Bundle builder: cart.js sync skipped after rate limit or busy server', err.message);
        } else {
          console.error(err);
        }
      });
  }

  /**
   * @param {{ productFormComponent: HTMLElement, form: HTMLFormElement, variantInput: HTMLInputElement | null }} ctx
   * @param {number[]} bundleVariantIds
   * @param {SubmitEvent} originalEvent
   */
  doBundleAddToCart(ctx, bundleVariantIds, originalEvent) {
    if (this.#bundleAddInFlight) return;
    this.#bundleAddInFlight = true;

    if (this.#cartErrorHideTimeoutId) {
      clearTimeout(this.#cartErrorHideTimeoutId);
      this.#cartErrorHideTimeoutId = undefined;
    }

    const { productFormComponent, variantInput } = ctx;
    const mainVariantId = this.getResolvedMainVariantId(ctx);

    if (variantInput && mainVariantId != null) {
      variantInput.value = String(mainVariantId);
    }

    const items = [];
    if (mainVariantId) items.push({ id: mainVariantId, quantity: 1 });
    bundleVariantIds.forEach((id) => items.push({ id, quantity: 1 }));

    const sectionsParam = this.getCartSectionsParameter();
    const payload = { items };
    if (sectionsParam) {
      payload.sections = sectionsParam;
      // Ensures sections render in page context when Referer is missing/stripped (Cart API docs).
      payload.sections_url = window.location.pathname;
    }

    const formEl = ctx.form;
    let addButton = this.#getAddToCartButton(formEl);
    let weDisabledAddButton = false;
    if (addButton && !addButton.disabled) {
      addButton.setAttribute('disabled', 'true');
      weDisabledAddButton = true;
    }

    const addToCartTextError = productFormComponent.refs?.addToCartTextError;
    let addButtonEnableDelayMs = 0;

    fetch(`${Theme.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(async (r) => {
        const text = await r.text();
        let response = {};

        if (text) {
          try {
            response = JSON.parse(text);
          } catch {
            const isRateLimit = r.status === 429;
            if (isRateLimit) addButtonEnableDelayMs = 4000;
            const message = isRateLimit
              ? 'Too many requests. Please wait a few seconds and try again.'
              : 'Could not add to cart. Please try again.';
            this.#showBundleCartError(productFormComponent, message);
            this.#syncCartUiFromServer(productFormComponent, undefined, { afterFailure: true });
            return;
          }
        }

        if (!r.ok) {
          if (r.status === 429) addButtonEnableDelayMs = 4000;
          const message =
            response.message ||
            response.description ||
            (r.status === 429
              ? 'Too many requests. Please wait a few seconds and try again.'
              : `Could not add to cart (${r.status}). Please try again.`);
          this.#showBundleCartError(productFormComponent, message);
          this.#syncCartUiFromServer(productFormComponent, undefined, { afterFailure: true });
          return;
        }

        if (response.status) {
          const message = response.message || response.description || '';
          this.#showBundleCartError(productFormComponent, message);
          this.#syncCartUiFromServer(productFormComponent, undefined, { afterFailure: true });
          return;
        }

        if (!Array.isArray(response.items) && (response.description || response.message)) {
          const message = response.description || response.message || '';
          this.#showBundleCartError(productFormComponent, message);
          this.#syncCartUiFromServer(productFormComponent, undefined, { afterFailure: true });
          return;
        }

        const id = mainVariantId != null ? String(mainVariantId) : variantInput?.value?.toString() || '';
        try {
          if (id) localStorage.setItem('lastVariantAdded', id);
          if (typeof productFormComponent.updateCallOuts === 'function') {
            productFormComponent.updateCallOuts();
          }
        } catch (err) {
          console.error(err);
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const btnForSuccess = this.#getAddToCartButton(formEl) || addButton;
        if (btnForSuccess) {
          const addedTextElement = btnForSuccess.querySelector('.add-to-cart-text--added');
          const addedText =
            addedTextElement?.textContent?.trim() || Theme.translations.added || '';
          const successLiveRegion = productFormComponent.refs?.liveRegion;
          if (successLiveRegion && addedText) {
            successLiveRegion.textContent = addedText;
            window.setTimeout(() => {
              successLiveRegion.textContent = '';
            }, 5000);
          }
        }

        this.#syncCartUiFromServer(productFormComponent, response.sections);
      })
      .catch((err) => {
        console.error(err);
        this.#showBundleCartError(
          productFormComponent,
          err?.message || 'Could not add to cart. Please try again.'
        );
        this.#syncCartUiFromServer(productFormComponent, undefined, { afterFailure: true });
      })
      .finally(() => {
        this.#bundleAddInFlight = false;

        const enableButton = () => {
          if (!weDisabledAddButton) return;
          const liveBtn = this.#getAddToCartButton(this.findProductFormContext()?.form) || addButton;
          if (liveBtn) {
            liveBtn.removeAttribute('disabled');
            liveBtn.disabled = false;
          }
        };
        if (addButtonEnableDelayMs > 0) {
          window.setTimeout(enableButton, addButtonEnableDelayMs);
        } else {
          enableButton();
        }

        if (typeof productFormComponent.updateCallOuts === 'function') {
          productFormComponent.updateCallOuts();
        }
        cartPerformance.measureFromEvent('add:user-action', originalEvent);
      });
  }

  /** Choices.js templates: thumbnail + label (shared by product and variant selects). */
  #bundleChoicesThumbTemplatesFactory() {
    const decodeAmp = (str) => (str || '').replace(/&amp;/g, '&');
    return function () {
      return {
        choice: function (config, choice, selectText) {
          const div = document.createElement('div');
          div.id = choice.elementId;
          div.className = `${config.classNames.item} ${config.classNames.itemChoice}`;
          if (choice.selected) div.classList.add(config.classNames.selectedState);
          if (choice.placeholder) div.classList.add(config.classNames.placeholder);
          div.setAttribute('role', choice.groupId > 0 ? 'treeitem' : 'option');
          Object.assign(div.dataset, {
            choice: '',
            id: choice.id,
            value: choice.value,
            selectText: selectText || config.itemSelectText,
          });
          if (choice.disabled) {
            div.classList.add(config.classNames.itemDisabled);
            div.dataset.choiceDisabled = '';
            div.setAttribute('aria-disabled', 'true');
          } else {
            div.classList.add(config.classNames.itemSelectable);
            div.dataset.choiceSelectable = '';
          }
          if (choice.customProperties && choice.customProperties.thumb) {
            const img = document.createElement('img');
            img.className = 'choices__thumb';
            img.src = choice.customProperties.thumb;
            img.alt = '';
            div.appendChild(img);
          }
          const label = document.createElement('span');
          label.className = 'choices__label';
          label.textContent = decodeAmp(choice.label);
          if (choice.label) label.setAttribute('title', decodeAmp(choice.label));
          div.appendChild(label);
          if (choice.selected && !choice.placeholder) {
            const selectedEl = document.createElement('span');
            selectedEl.className = 'bundle-builder__choices-dropdown-selected';
            selectedEl.setAttribute('aria-hidden', 'true');
            selectedEl.textContent =
              (typeof Theme !== 'undefined' && Theme.translations?.bundle_choice_selected) || 'Selected';
            div.appendChild(selectedEl);
          }
          return div;
        },
        item: function (config, item, removeItemButton) {
          const div = document.createElement('div');
          div.className = config.classNames.item;
          div.dataset.item = '';
          div.dataset.id = item.id;
          div.dataset.value = item.value;
          if (item.customProperties) div.dataset.customProperties = JSON.stringify(item.customProperties);
          if (item.active) div.setAttribute('aria-selected', 'true');
          if (item.disabled) div.setAttribute('aria-disabled', 'true');
          if (item.placeholder) div.classList.add(config.classNames.placeholder);
          div.classList.add(item.highlighted ? config.classNames.highlightedState : config.classNames.itemSelectable);
          if (item.customProperties && item.customProperties.thumb) {
            const img = document.createElement('img');
            img.className = 'choices__thumb';
            img.src = item.customProperties.thumb;
            img.alt = '';
            div.appendChild(img);
          }
          const label = document.createElement('span');
          label.className = 'choices__label';
          label.textContent = decodeAmp(item.label);
          if (item.label) label.setAttribute('title', decodeAmp(item.label));
          div.appendChild(label);
          if (removeItemButton) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = config.classNames.button;
            btn.setAttribute('aria-label', `Remove item: '${item.value}'`);
            btn.dataset.button = '';
            div.appendChild(btn);
          }
          return div;
        },
      };
    };
  }

  initProductChoices() {
    if (this._productChoicesInited || typeof window.Choices !== 'function') return;

    const thumbTemplates = this.#bundleChoicesThumbTemplatesFactory();

    this.querySelectorAll('.bundle-builder__select--product').forEach((select) => {
      if (select.closest('.bundle-builder__select-wrap--single-product-pool')) return;
      if (select.closest('.choices')) return;
      try {
        const instance = new window.Choices(select, {
          searchEnabled: false,
          allowHTML: false,
          itemSelectText: '',
          callbackOnCreateTemplates: thumbTemplates,
        });
        select._choicesInstance = instance;
      } catch (err) {
        console.warn('Bundle builder Choices init:', err);
      }
    });

    this.querySelectorAll('.bundle-builder__select--variant').forEach((variantSelect) => {
      const sellable = Array.from(variantSelect.options).some((o) => o.value && !o.disabled);
      if (!sellable || variantSelect._choicesInstance) return;
      this.initVariantChoices(variantSelect);
    });

    this._productChoicesInited = true;
  }

  initVariantChoices(variantSelect) {
    if (typeof window.Choices !== 'function' || variantSelect.closest('.choices')) return;
    const builder = this;
    try {
      const instance = new window.Choices(variantSelect, {
        searchEnabled: false,
        allowHTML: false,
        itemSelectText: '',
        callbackOnCreateTemplates: this.#bundleChoicesThumbTemplatesFactory(),
        callbackOnChange: function () {
          builder.updateTotals();
        },
      });
      variantSelect._choicesInstance = instance;
    } catch (err) {
      console.warn('Bundle builder variant Choices init:', err);
    }
  }

  destroyVariantChoices(variantSelect) {
    const choicesEl = variantSelect.closest('.choices') || variantSelect.parentElement;
    if (choicesEl) choicesEl.classList.remove('choices--single-variant');
    const instance = variantSelect._choicesInstance;
    if (instance && typeof instance.destroy === 'function') {
      instance.destroy();
      variantSelect._choicesInstance = null;
    }
  }

  openSlot(trigger) {
    const slot = trigger.dataset.slot;
    const slotsPanel = this.querySelector(`.bundle-builder__slots[data-slot="${slot}"]`);
    if (!slotsPanel) return;

    const wrap = trigger.closest('.bundle-builder__wrap');
    if (!wrap) return;

    trigger.setAttribute('aria-expanded', 'true');
    slotsPanel.classList.remove('hidden');
    slotsPanel.setAttribute('aria-hidden', 'false');
    wrap.classList.add('bundle-builder__wrap--open');

    if (wrap.querySelector('.bundle-builder__slot--single-product')) {
      const productSelect = this.querySelector(`.bundle-builder__select--product[data-slot="${slot}"]`);
      const variantSelect = this.querySelector(`.bundle-builder__select--variant[data-slot="${slot}"]`);
      if (
        productSelect &&
        variantSelect &&
        !String(variantSelect.value || '').trim()
      ) {
        const productId = parseInt(productSelect.value, 10);
        if (Number.isFinite(productId)) {
          this.onProductChange(productSelect);
        }
      }
    }
  }

  deselectSlot(trigger) {
    const wrap = trigger.closest('.bundle-builder__wrap');
    if (!wrap) return;
    const slot = wrap.dataset.slot;
    const productSelect = this.querySelector(`.bundle-builder__select--product[data-slot="${slot}"]`);
    const variantSelect = this.querySelector(`.bundle-builder__select--variant[data-slot="${slot}"]`);
    const isSingle = Boolean(wrap.querySelector('.bundle-builder__slot--single-product'));

    if (productSelect) {
      if (isSingle) {
        const slotData = this.bundleData.find((s) => String(s.slot) === String(slot));
        const onlyProduct = slotData?.products?.[0];
        if (onlyProduct) {
          productSelect.value = String(onlyProduct.id);
          const productChoices = productSelect._choicesInstance;
          if (productChoices && typeof productChoices.setChoiceByValue === 'function') {
            productChoices.setChoiceByValue(String(onlyProduct.id));
          }
        }
      } else {
        productSelect.value = '';
        const productChoices = productSelect._choicesInstance;
        if (productChoices && typeof productChoices.setChoiceByValue === 'function') {
          productChoices.setChoiceByValue('');
        }
      }
    }

    if (variantSelect) {
      this.destroyVariantChoices(variantSelect);
      if (isSingle && productSelect?.value) {
        this.onProductChange(productSelect);
      } else {
        this.#resetVariantSelectPlaceholder(variantSelect);
        variantSelect.disabled = true;
      }
    }

    const slotsPanel = this.querySelector(`.bundle-builder__slots[data-slot="${slot}"]`);
    if (slotsPanel) {
      trigger.setAttribute('aria-expanded', 'false');
      slotsPanel.classList.add('hidden');
      slotsPanel.setAttribute('aria-hidden', 'true');
      wrap.classList.remove('bundle-builder__wrap--open');
    }

    this.updateTotals();
  }

  onProductChange(productSelect) {
    const slot = productSelect.dataset.slot;
    const variantSelect = this.querySelector(`.bundle-builder__select--variant[data-slot="${slot}"]`);
    if (!variantSelect) return;

    this.destroyVariantChoices(variantSelect);

    const productId = parseInt(productSelect.value, 10);
    const slotData = this.bundleData.find((s) => String(s.slot) === String(slot));
    const productData = slotData?.products.find((p) => p.id === productId);

    this.#resetVariantSelectPlaceholder(variantSelect);

    if (productData) {
      productData.variants.forEach((v) => {
        const opt = document.createElement('option');
        opt.value = v.id;
        opt.dataset.price = v.price;
        opt.textContent = v.title === 'Default Title' ? 'One Color' : v.title;
        if (!v.available) {
          opt.disabled = true;
          opt.textContent += ' — Sold out';
        }
        const thumbUrl = v.thumb || productData.thumb;
        if (thumbUrl) {
          opt.setAttribute('data-custom-properties', JSON.stringify({ thumb: thumbUrl }));
        }
        variantSelect.appendChild(opt);
      });
      variantSelect.disabled = false;

      if (typeof window.Choices === 'function') {
        this.initVariantChoices(variantSelect);
      }
    } else {
      variantSelect.disabled = true;
    }

    this.updateTotals();
  }

  getSelectedVariants() {
    return Array.from(this.querySelectorAll('.bundle-builder__select--variant'))
      .map((s) => s.value)
      .filter((value) => value != null && String(value).trim() !== '');
  }

  formatMoney(cents) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';
    const fmt = (window.theme?.settings?.money_format || '${{amount}}')
      .replace('{{amount}}', (amount / 100).toFixed(2))
      .replace('{{amount_no_decimals}}', Math.round(amount / 100))
      .replace('{{amount_with_comma_separator}}', (amount / 100).toFixed(2).replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(amount / 100));
    return fmt;
  }

  getVariantPrice(variantId) {
    for (const slotData of this.bundleData) {
      for (const product of slotData.products) {
        const variant = product.variants.find((v) => String(v.id) === String(variantId));
        if (variant) return variant.price;
      }
    }
    return 0;
  }

  getMainPrice() {
    const ctx = this.findProductFormContext();
    const variantId = this.getResolvedMainVariantId(ctx);
    if (variantId == null) return 0;
    const main = this.mainVariants.find((v) => v.id === variantId);
    return main ? main.price : 0;
  }

  updateTotals() {
    if (!this.totalsBlock || !this.totalsPrice) return;

    const selectedVariants = this.getSelectedVariants();
    const bundleCount = selectedVariants.length;
    const totalItems = bundleCount + 1;

    if (bundleCount === 0) {
      this.totalsBlock.classList.add('hidden');
      return;
    }

    const discountPercent = bundleCount === 1 ? this.discount1 : this.discount2;
    const discountMultiplier = 1 - discountPercent / 100;

    const mainPrice = this.getMainPrice();
    const mainDiscounted = Math.ceil(mainPrice * discountMultiplier);

    const bundleDiscountedTotal = selectedVariants.reduce((sum, id) => {
      const itemPrice = this.getVariantPrice(id);
      return sum + Math.ceil(itemPrice * discountMultiplier);
    }, 0);

    const finalSavedPrice = mainDiscounted + bundleDiscountedTotal;

    const fullPriceUnrounded =
      mainPrice + selectedVariants.reduce((sum, id) => sum + this.getVariantPrice(id), 0);

    if (this.totalsLabel) {
      this.totalsLabel.textContent = `Total (${totalItems} item${totalItems !== 1 ? 's' : ''}): `;
    }

    this.totalsPrice.textContent = this.formatMoney(finalSavedPrice);

    if (this.totalsCompare) {
      this.totalsCompare.textContent =
        fullPriceUnrounded !== finalSavedPrice ? this.formatMoney(fullPriceUnrounded) : '';
      this.totalsCompare.style.display = fullPriceUnrounded !== finalSavedPrice ? '' : 'none';
    }
    this.totalsBlock.classList.remove('hidden');
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
